import React from 'react';
import { useTenantBranding } from '../../providers/TenantBrandingProvider';
import { useTheme, useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

/**
 * "Powered by X" footer.
 *
 * Renders the platform attribution at the bottom of the app shell.
 * Reads its content from `useTenantBranding()` so the resolution logic
 * (brand default → tenant setting → storefront override) stays in one
 * place.
 *
 * 2026-05-23 — added per user direction: "we are not getting much
 * credit for having our software in the hands of hundreds of users
 * who use it daily." Default-on across the platform; tenants can opt
 * out or relabel via the `branding.attribution` instance setting in
 * the config-web admin.
 *
 * Visually subtle by design — small text, low contrast, no border.
 * Doesn't compete with primary content but stays legible.
 *
 * Hidden below the `md` breakpoint (PD-13660): the credit adds little
 * on a phone and costs scarce vertical space, so it shows only at
 * tablet/desktop widths. Mounted at the bottom of the right content
 * pane (see App.tsx), so it stays within that pane and clear of the
 * left nav.
 */

interface BrandAttributionFooterProps {
	className?: string;
}

export function BrandAttributionFooter({
	className,
}: BrandAttributionFooterProps): React.ReactElement | null {
	const branding = useTenantBranding();
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();

	if (!branding.attribution.show) {
		return null;
	}

	const { label, logoUrl, logoUrlDark, linkUrl: href } = branding.attribution;
	// Copyright year is computed at render time so the footer self-
	// updates on Jan 1 without a deploy. Tests that pin the year can
	// mock `Date` if they ever need a stable snapshot.
	const currentYear = new Date().getFullYear();

	const textClasses = cn(
		'text-[11px] leading-none tracking-wide',
		// The default subtle tertiary text (dark:text-gray-500) is unreadable on a
		// brand-tinted dark surface like the storefront's dark page bg, so use a
		// much lighter gray in dark mode. Light mode keeps the subtle tertiary,
		// which reads fine on the light page bg.
		isDark ? 'text-gray-300' : colors.classes.textTertiary,
	);

	// The footer renders a fixed "Powered by " prefix followed by the
	// brand mark (logo image when configured, otherwise the brand-name
	// text). `label` is the brand NAME only going forward, but legacy
	// brand defaults and tenant overrides stored the full "Powered by
	// X" string — strip a leading "Powered by " so those still render
	// as "Powered by X" instead of doubling the prefix.
	const brandName = label.replace(/^\s*powered by\s+/i, '').trim();

	// Logo (small wordmark, ~14px tall to match the email templates)
	// replaces the brand-name text when a logo is set. In dark mode the
	// standard wordmark (dark text) is near-invisible on the dark footer,
	// so prefer the dark-variant logo when one is configured, falling back
	// to the standard logo otherwise (PD-13660 follow-up).
	const resolvedLogoUrl =
		isDark && logoUrlDark != null && logoUrlDark !== '' ? logoUrlDark : logoUrl;
	const brandMark =
		resolvedLogoUrl != null && resolvedLogoUrl !== '' ? (
			<img
				src={resolvedLogoUrl}
				alt={t(brandName !== '' ? brandName : branding.brandDisplayName)}
				className="inline-block h-3.5 w-auto align-middle"
			/>
		) : (
			<span>{t(brandName)}</span>
		);

	// "Powered by <brand>" is the clickable / brand-attributed part.
	// When `linkUrl` is null/empty it collapses to a span — Ice Mover
	// does this today; Workd renders as an anchor to workd.com.
	const attribution = (
		<span className="inline-flex items-center gap-1">
			<span>{t('Powered by')}</span>
			{brandMark}
		</span>
	);

	const attributionNode =
		href != null && href !== '' ? (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className={cn(textClasses, 'inline-flex items-center hover:underline')}
			>
				{attribution}
			</a>
		) : (
			<span className={cn(textClasses, 'inline-flex items-center')}>
				{attribution}
			</span>
		);

	return (
		<footer
			className={cn(
				// Subtle top border so page content above ends cleanly
				// instead of dropping straight into the footer; the footer
				// has no fill, so it still inherits the right pane surface.
				// Balanced py + items-center keep "Powered by" vertically
				// centered between the border and the screen bottom (PD-13660).
				'hidden md:flex shrink-0 justify-center items-center gap-2 border-t py-2 px-3 select-none',
				colors.classes.borderDefault,
				className,
			)}
			aria-label={t('Brand attribution')}
		>
			{attributionNode}
			{/* 2026-05-26 — dropped the bullet separator between the
			    "Powered by X" label and the copyright. The © symbol
			    itself reads as a visual divider, so the extra middot
			    just added noise (per user direction). The copyright
			    carries the standard "All Rights Reserved" notice; the
			    brand name already appears above so it isn't repeated. */}
			<span className={textClasses}>
				{t('©')} {currentYear} {t('All Rights Reserved')}
			</span>
		</footer>
	);
}
